import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../../components/Layout';
import { Ic } from '../../design/icons';
import { Chip } from '../../design/primitives';

const API = `${window.location.origin}/api`;

const STATUS_LABELS = {
  queued: 'Kuyrukta',
  sending: 'Gönderiliyor',
  completed: 'Tamamlandı',
  failed: 'Başarısız',
};

const AUDIENCE_LABELS = {
  all_users: 'Tüm Kullanıcılar',
  active_subscribers: 'Aktif Aboneler',
  plan: 'Plan',
  workspace: 'Workspace',
  manual: 'Manuel',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  );
}

function formatRelative(iso) {
  if (!iso) return '';
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'Az önce';
  if (diffMin < 60) return `${diffMin} dk önce`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.floor(h / 24);
  return `${d} gün önce`;
}

function statusChip(status) {
  if (status === 'completed') return <Chip color="var(--good)" bg="var(--good-weak)">{STATUS_LABELS.completed}</Chip>;
  if (status === 'failed') return <Chip color="var(--danger)" bg="var(--surface-muted)">{STATUS_LABELS.failed}</Chip>;
  if (status === 'sending') return <Chip color="var(--accent)" bg="var(--surface-muted)">{STATUS_LABELS.sending}</Chip>;
  return <Chip color="var(--text-muted)" bg="var(--surface-muted)">{STATUS_LABELS.queued}</Chip>;
}

export default function EmailCampaignHistory() {
  usePageHeader([{ label: 'Süper Admin' }, { label: 'Kampanya Geçmişi' }], null, []);

  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRawHtml, setShowRawHtml] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const res = await axios.get(`${API}/super-admin/campaigns?${params.toString()}`);
      setCampaigns(res.data.campaigns || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kampanyalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Poll while any campaign is queued/sending
  const pollRef = useRef(null);
  useEffect(() => {
    const inFlight = campaigns.some((c) => c.status === 'queued' || c.status === 'sending');
    if (inFlight) {
      pollRef.current = setTimeout(() => fetchList(), 3000);
    }
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [campaigns, fetchList]);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/campaigns/${id}`);
      setDetail(res.data);
    } catch (err) {
      toast.error('Detay yüklenemedi');
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (detailId) {
      fetchDetail(detailId);
      setShowRawHtml(false);
    } else {
      setDetail(null);
    }
  }, [detailId, fetchDetail]);

  // Poll detail while campaign is sending
  useEffect(() => {
    if (!detail?.campaign) return;
    if (detail.campaign.status !== 'queued' && detail.campaign.status !== 'sending') return;
    const t = setTimeout(() => fetchDetail(detail.campaign.id), 3000);
    return () => clearTimeout(t);
  }, [detail, fetchDetail]);

  const stats = useMemo(() => {
    const sending = campaigns.filter((c) => c.status === 'queued' || c.status === 'sending').length;
    const completed = campaigns.filter((c) => c.status === 'completed').length;
    const failed = campaigns.filter((c) => c.status === 'failed').length;
    const totalRecipients = campaigns.reduce((acc, c) => acc + (c.recipientCount || 0), 0);
    const totalSuccess = campaigns.reduce((acc, c) => acc + (c.successCount || 0), 0);
    return { sending, completed, failed, totalRecipients, totalSuccess };
  }, [campaigns]);

  return (
    <div className="ox-page space-y-4" style={{ padding: 24 }}>
      <div className="ox-grid-stats-6">
        <StatTile label="Toplam (filtreli)" value={total} />
        <StatTile label="İşlemde" value={stats.sending} color="var(--accent)" />
        <StatTile label="Tamamlanan" value={stats.completed} color="var(--good)" />
        <StatTile label="Başarısız" value={stats.failed} color="var(--danger)" />
        <StatTile label="Bu sayfada alıcı" value={stats.totalRecipients} small />
        <StatTile label="Başarılı gönderim" value={stats.totalSuccess} small color="var(--good)" />
      </div>

      <div className="ox-surface rounded-md p-3 flex items-center gap-2 flex-wrap">
        <select className="ox-select" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={{ maxWidth: 200 }}>
          <option value="all">Tüm Durumlar</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Link to="/super-admin/campaigns" className="ox-btn ml-auto">
          {Ic.bell || '+'} Yeni Kampanya
        </Link>
        <button className="ox-btn ox-btn-ghost" onClick={fetchList} title="Yenile">{Ic.refresh}</button>
      </div>

      <div className="ox-surface rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center ox-text-muted">Yükleniyor...</div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center ox-text-muted">Henüz kampanya gönderilmedi</div>
        ) : (
          <div className="ox-table-wrap ox-table-wide">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Konu</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Gönderen</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Hedef</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium ox-tnum">Sonuç</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Durum</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden lg:table-cell">Zaman</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => setDetailId(c.id)}
                  >
                    <td className="px-3 py-2.5 align-top">
                      <div className="text-[12.5px] font-medium truncate" style={{ maxWidth: 360 }}>{c.subject}</div>
                      <div className="text-[11px] ox-text-muted">{c.id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-3 py-2.5 align-top hidden md:table-cell">
                      <div className="text-[12.5px] truncate" style={{ maxWidth: 160 }}>{c.sentByName || '—'}</div>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <Chip color="var(--accent)" bg="var(--surface-muted)">
                        {AUDIENCE_LABELS[c.audienceType] || c.audienceType}
                      </Chip>
                    </td>
                    <td className="px-3 py-2.5 align-top ox-tnum">
                      <div className="text-[12.5px]">
                        <span style={{ color: 'var(--good)' }}>{c.successCount || 0}</span>
                        {' / '}
                        <span>{c.recipientCount || 0}</span>
                      </div>
                      {(c.failCount || 0) > 0 && (
                        <div className="text-[11px]" style={{ color: 'var(--danger)' }}>
                          {c.failCount} hata
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">{statusChip(c.status)}</td>
                    <td className="px-3 py-2.5 align-top hidden lg:table-cell">
                      <div className="text-[12px]">{formatDate(c.createdAt)}</div>
                      <div className="text-[11px] ox-text-muted">{formatRelative(c.createdAt)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
            <span className="text-[12px] ox-text-muted">
              {(page - 1) * 25 + 1}–{Math.min(page * 25, total)} / {total}
            </span>
            <div className="flex items-center gap-1">
              <button className="ox-btn ox-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Önceki</button>
              <button className="ox-btn ox-btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki</button>
            </div>
          </div>
        )}
      </div>

      {detailId && (
        <div className="ox-modal-backdrop" onClick={() => setDetailId(null)}>
          <div className="ox-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px] truncate" style={{ maxWidth: 540 }}>
                {detail?.campaign?.subject || 'Kampanya'}
              </div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setDetailId(null)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {detailLoading && !detail ? (
                <div className="text-[12.5px] ox-text-muted p-6 text-center">Yükleniyor...</div>
              ) : detail ? (
                <div className="space-y-3">
                  <div className="ox-grid-stats-6">
                    <StatTile label="Alıcı" value={detail.campaign.recipientCount || 0} />
                    <StatTile label="Başarılı" value={detail.counts?.sent || 0} color="var(--good)" />
                    <StatTile label="Hata" value={detail.counts?.failed || 0} color="var(--danger)" />
                    <StatTile label="Bekleyen" value={detail.counts?.pending || 0} small />
                  </div>
                  <div className="text-[12px] ox-text-muted">
                    Gönderen: <strong>{detail.campaign.sentByName || '—'}</strong>
                    {' · '}Hedef: <strong>{AUDIENCE_LABELS[detail.campaign.audienceType] || detail.campaign.audienceType}</strong>
                    {' · '}Durum: {statusChip(detail.campaign.status)}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] ox-text-muted uppercase tracking-wider">Mail içeriği</div>
                      <button
                        className="ox-btn ox-btn-ghost text-[11.5px]"
                        onClick={() => setShowRawHtml((v) => !v)}
                      >
                        {showRawHtml ? 'Önizleme' : 'HTML kaynağı'}
                      </button>
                    </div>
                    {showRawHtml ? (
                      <pre
                        className="ox-surface rounded-md p-2.5 text-[12px]"
                        style={{
                          border: '1px solid var(--border)',
                          maxHeight: 280,
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'ui-monospace, Menlo, monospace',
                        }}
                      >
                        {detail.campaign.bodyHtml || ''}
                      </pre>
                    ) : (
                      <iframe
                        title={`campaign-body-${detail.campaign.id}`}
                        sandbox=""
                        srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:'Segoe UI',sans-serif;margin:0;padding:14px;color:#222;background:#fff}</style></head><body>${detail.campaign.bodyHtml || '<p style="color:#999">İçerik yok</p>'}</body></html>`}
                        style={{
                          width: '100%',
                          height: 280,
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          background: '#fff',
                        }}
                      />
                    )}
                  </div>
                  <div>
                    <div className="text-[11px] ox-text-muted uppercase tracking-wider mb-1">Alıcı kayıtları</div>
                    <div className="ox-surface rounded-md" style={{ border: '1px solid var(--border)', maxHeight: 320, overflowY: 'auto' }}>
                      {(detail.recipients || []).slice(0, 500).map((r) => (
                        <div
                          key={r.id}
                          className="flex items-center gap-2 px-2.5 py-1.5"
                          style={{ borderBottom: '1px solid var(--border)' }}
                        >
                          <span className="text-[12px] truncate flex-1">{r.email}</span>
                          {r.status === 'sent' && <Chip color="var(--good)" bg="var(--good-weak)">Gönderildi</Chip>}
                          {r.status === 'failed' && <Chip color="var(--danger)" bg="var(--surface-muted)">Hata</Chip>}
                          {r.status === 'pending' && <Chip color="var(--text-muted)" bg="var(--surface-muted)">Bekliyor</Chip>}
                          {r.error && (
                            <span className="text-[11px]" style={{ color: 'var(--danger)', maxWidth: 220 }} title={r.error}>
                              {r.error.slice(0, 60)}
                            </span>
                          )}
                        </div>
                      ))}
                      {(detail.recipients || []).length === 0 && (
                        <div className="text-[12px] ox-text-muted p-3 text-center">Alıcı yok</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" onClick={() => setDetailId(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, color, small }) {
  return (
    <div className="ox-surface rounded-md p-3">
      <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">{label}</div>
      <div
        className="ox-tnum font-semibold mt-1"
        style={{ color: color || 'var(--text)', fontSize: small ? 16 : 22 }}
      >
        {value}
      </div>
    </div>
  );
}
